var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, TableCell, TableRow, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { api, apiResolve, CoreEvent, useDebounce, WrappedTableCell } from '@sierralabs/core-ui';
import { getEntityProperties } from '@sierralabs/entity-core';
import { useAssignEntity, useEntityContext } from '@sierralabs/entity-ui';
import { applyQueryFilters, useQueryContext } from '@sierralabs/query-ui';
import SiteTableRenderer, { formatComponent } from '../common/table/SiteTableRenderer';
import { getQueryValue } from '../hooks/component/useQueryDisplayValue';
import { useSiteTablePagination } from '../hooks/site-table/useSiteTablePagination';
import { AssignEntityEvent, useAssignEntityComponent } from '../plugins/components/AssignEntityPlugin';
import { createSiteTableFilterExpressionMap } from '../plugins/components/SiteTablePlugin';
import { useSiteContext } from '../SiteContext';
function useRunCardTableQuery(component, open) {
    var _a;
    const params = useParams();
    const [queryData, setQueryData] = useState();
    const { queries } = useQueryContext();
    const table = component === null || component === void 0 ? void 0 : component.props;
    const queryId = (_a = component === null || component === void 0 ? void 0 : component.queryIds) === null || _a === void 0 ? void 0 : _a[0];
    const query = queryId
        ? queries.find(query => query.id === queryId)
        : undefined;
    // run query for card table here to save it in context
    const { filter } = table || {};
    const filterTarget = (filter === null || filter === void 0 ? void 0 : filter.routeParam)
        ? params[filter === null || filter === void 0 ? void 0 : filter.routeParam]
        : undefined;
    const runCardQuery = useCallback(() => {
        // NOTE: use promise request since runQuery() messes up
        // data when called more than once
        const fetch = () => __awaiter(this, void 0, void 0, function* () {
            const expressionMap = filter &&
                createSiteTableFilterExpressionMap(filter.field || '', filterTarget);
            const { data } = yield apiResolve(api.post(`queries/${queryId}/run`, { expressionMap }));
            setQueryData(data);
        });
        fetch();
    }, [queryId, filter, filterTarget]);
    useEffect(() => {
        !open && setQueryData(undefined);
    }, [open]);
    useEffect(() => {
        open && runCardQuery();
    }, [open, runCardQuery]);
    return { query, queryData, queryId, runCardQuery };
}
export const AssignEntityDialog = props => {
    var _a, _b, _c, _d, _e, _f;
    const { open, multiple = false, title = 'Assign Entity', component, tableComponent, assignConfig, onCancel, onAssign } = props;
    const onAssignRef = useRef(onAssign);
    const [search, setSearch] = useState('');
    const { queries, queryResults, getRows, runQueryOrPaginated } = useQueryContext();
    const debouncedSearch = useDebounce(search || '', 300);
    const [selectedItems, setSelectedItems] = useState([]);
    // if query is paginated, some selected items may not be
    // included in the queryResult data and may be filtered out.
    // avoid deselecting them by including those items as hidden items
    const [hiddenItems, setHiddenItems] = useState([]);
    const { pagination, setPagination } = useSiteTablePagination();
    const { editing, plugins, isHover, hoverProps } = useSiteContext();
    const { entities } = useEntityContext();
    const assignEntityComponent = useAssignEntityComponent(component);
    const { entity: updatedEntity, isAssigning, assignError, assignEntity } = useAssignEntity((assignConfig === null || assignConfig === void 0 ? void 0 : assignConfig.props) || {});
    const tableClasses = ['site-table'];
    if (tableComponent && isHover(tableComponent)) {
        tableClasses.push('component-hover');
    }
    const { queryId } = assignConfig || {};
    const { columns = [] } = (assignConfig === null || assignConfig === void 0 ? void 0 : assignConfig.table) || {};
    const assign = (assignConfig === null || assignConfig === void 0 ? void 0 : assignConfig.props) || {};
    const { filterColumns } = assign;
    const selectedEntity = assign.tableName
        ? entities === null || entities === void 0 ? void 0 : entities.find(entity => entity.tableName === assign.tableName)
        : undefined;
    const properties = getEntityProperties(assign.relations || [], selectedEntity, entities || []);
    const lastProperty = properties[properties.length - 1];
    const assignEntityName = ((_a = lastProperty.relationSchema) === null || _a === void 0 ? void 0 : _a.name) || 'Entity';
    const entity = lastProperty.relationSchema;
    // fields to match dialog table data and card table data
    const idColumn = ((_b = ((entity === null || entity === void 0 ? void 0 : entity.columns) || []).find(column => column.primary)) === null || _b === void 0 ? void 0 : _b.name) || 'id';
    const cardTableField = props.cardTableField || idColumn;
    const dialogTableField = props.dialogTableField || idColumn;
    // dialog table query result
    const query = queryId
        ? queries.find(query => query.id === queryId)
        : undefined;
    const queryResult = queryId
        ? queryResults.find(result => result.queryId === queryId)
        : undefined;
    // get table component from assignEntity -> card -> table
    const cardTable = (_e = (_d = (_c = assignEntityComponent === null || assignEntityComponent === void 0 ? void 0 : assignEntityComponent.children) === null || _c === void 0 ? void 0 : _c.find(c => c.slot === 'card')) === null || _d === void 0 ? void 0 : _d.children) === null || _e === void 0 ? void 0 : _e.find(c => c.slot === 'table');
    const { query: cardQuery, queryData: cardQueryData, runCardQuery } = useRunCardTableQuery(cardTable, open);
    const hidePagination = (_f = tableComponent === null || tableComponent === void 0 ? void 0 : tableComponent.props) === null || _f === void 0 ? void 0 : _f.hidePagination;
    const getValue = useCallback((item, q = query, field = dialogTableField) => {
        return q ? getQueryValue(item, q, field) : item[idColumn];
    }, [dialogTableField, idColumn, query]);
    const isSelected = (item) => {
        return selectedItems.some(selectedItem => getValue(selectedItem) === getValue(item));
    };
    // get data not selected
    const filteredItems = (queryResult ? getRows(queryResult) : []).filter((item) => !isSelected(item));
    const getQueryIndex = (item) => {
        const rows = queryResult ? getRows(queryResult) : [];
        return rows.findIndex(data => getValue(data) === getValue(item));
    };
    const renderRows = (items, includeQueryRow = false) => {
        if (!tableComponent) {
            return;
        }
        return items.map(item => {
            const selected = isSelected(item);
            return (React.createElement(TableRow, { hover: true, role: 'checkbox', color: 'primary', selected: selected, key: getValue(item), onClick: () => {
                    // handle toggle select
                    setSelectedItems(items => {
                        return selected
                            ? items.filter(i => getValue(i) !== getValue(item))
                            : multiple
                                ? items.concat(item)
                                : [item];
                    });
                } },
                multiple && (React.createElement(TableCell, { padding: 'checkbox' },
                    React.createElement(Checkbox, { color: 'primary', checked: selected }))),
                columns.map(column => (React.createElement(WrappedTableCell, { key: column.id }, formatComponent(tableComponent, plugins, column, {
                    index: getQueryIndex(item),
                    row: includeQueryRow ? item : undefined
                }))))));
        });
    };
    useEffect(() => {
        onAssignRef.current = onAssign;
    }, [onAssign]);
    useEffect(() => {
        if (!open) {
            setSearch('');
            setSelectedItems([]);
            setHiddenItems([]);
        }
    }, [open]);
    useEffect(() => {
        if (!open || !queryResult || !cardQueryData) {
            return;
        }
        const items = getRows(queryResult);
        const selectedItems = Array.isArray(cardQueryData)
            ? cardQueryData
            : cardQueryData
                ? [cardQueryData]
                : [];
        const selected = items.filter(item => {
            const value = getValue(item);
            return selectedItems.some(cardItem => {
                return value === getValue(cardItem, cardQuery, cardTableField);
            });
        });
        setSelectedItems(selected);
        // handle items that were filtered out (can occur for paginated queries)
        // also make sure to clear hiddenItems if both all selected items are handled
        const hiddenItems = selected.length === selectedItems.length
            ? []
            : selectedItems.filter(cardItem => {
                const value = getValue(cardItem, cardQuery, cardTableField);
                return !selected.some(item => value === getValue(item));
            });
        setHiddenItems(hiddenItems);
    }, [
        open,
        cardQuery,
        cardQueryData,
        cardTableField,
        queryResult,
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.data,
        getRows,
        getValue
    ]);
    const expressionMap = useMemo(() => {
        const expressionMap = { queryType: 'select' };
        if (debouncedSearch && filterColumns) {
            const where = {
                type: 'and',
                condition: filterColumns.map((column, index) => {
                    return {
                        type: index === 0 ? 'simple' : 'or',
                        condition: {
                            operator: 'ilike',
                            parameters: [column, `%${debouncedSearch}%`]
                        }
                    };
                })
            };
            expressionMap.wheres = (expressionMap.wheres || []).concat(where);
        }
        return applyQueryFilters(expressionMap, Object.assign(Object.assign({}, pagination), { paginated: !hidePagination }));
    }, [debouncedSearch, filterColumns, hidePagination, pagination]);
    const runQueryWithFilters = useCallback(() => {
        runQueryOrPaginated(queryId, !hidePagination, expressionMap);
    }, [expressionMap, hidePagination, queryId, runQueryOrPaginated]);
    useEffect(() => {
        runQueryWithFilters();
    }, [runQueryWithFilters]);
    useEffect(() => {
        var _a;
        if (updatedEntity) {
            (_a = onAssignRef.current) === null || _a === void 0 ? void 0 : _a.call(onAssignRef, updatedEntity);
        }
    }, [updatedEntity]);
    // handle refresh on open dialog event
    useEffect(() => {
        return CoreEvent.handle(AssignEntityEvent.OpenDialog, data => {
            if (data &&
                (assignEntityComponent === null || assignEntityComponent === void 0 ? void 0 : assignEntityComponent.id) &&
                data.id === assignEntityComponent.id) {
                runCardQuery();
                runQueryWithFilters();
            }
        });
    }, [assignEntityComponent === null || assignEntityComponent === void 0 ? void 0 : assignEntityComponent.id, runCardQuery, runQueryWithFilters]);
    return (React.createElement(Dialog, { open: open, onClose: onCancel, fullWidth: true, maxWidth: 'md' },
        isAssigning && React.createElement(LinearProgress, null),
        React.createElement(DialogTitle, null, title),
        React.createElement(DialogContent, { style: { padding: 0 } },
            React.createElement(Box, { mx: 3 },
                React.createElement(DialogContentText, null,
                    "Select ",
                    assignEntityName,
                    " to assign")),
            React.createElement(Box, { my: 1, mx: 3 },
                React.createElement("form", { autoComplete: 'off', onSubmit: event => event.preventDefault() },
                    React.createElement(TextField, { autoFocus: true, fullWidth: true, variant: 'outlined', autoComplete: 'off', disabled: isAssigning, label: `Search ${assignEntityName}`, InputProps: { readOnly: !!editing }, onChange: event => setSearch(event.target.value) }))),
            tableComponent && (React.createElement(SiteTableRenderer, { hideColumns: true, columns: columns, isLoading: isAssigning, queryResult: queryResult, component: tableComponent, hidePagination: hidePagination, colSpan: +multiple + columns.length, onPaginationChange: setPagination, tableProps: Object.assign(Object.assign({}, hoverProps(tableComponent)), { className: tableClasses.join(' ') }), renderHeader: columns => (React.createElement(React.Fragment, null,
                    multiple && React.createElement(TableCell, null),
                    columns)) },
                renderRows(selectedItems, true),
                renderRows(filteredItems))),
            React.createElement(Collapse, { in: !!assignError, style: { minHeight: 'unset' } },
                React.createElement(Box, { mt: 1, mx: 3 },
                    React.createElement(Alert, { severity: 'error' }, (assignError === null || assignError === void 0 ? void 0 : assignError.message) || 'An error occurred.')))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { disabled: isAssigning, onClick: onCancel }, "Cancel"),
            React.createElement(Button, { color: 'primary', variant: 'contained', disabled: isAssigning, onClick: () => {
                    if (editing) {
                        return;
                    }
                    // only include ids
                    const assignItems = selectedItems.map(item => {
                        return { [idColumn]: getValue(item) };
                    });
                    const cardItems = hiddenItems.map(item => {
                        return { [idColumn]: getValue(item, cardQuery, cardTableField) };
                    });
                    const allItems = assignItems.concat(cardItems);
                    assignEntity(multiple ? allItems : allItems[0] || null);
                } }, "Assign"))));
};
//# sourceMappingURL=AssignEntityDialog.js.map